"use client";

import React, {
  createContext,
  useState,
  ReactNode,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { usePathname } from "next/navigation";

// تعریف نوع فیش حقوقی
interface Salary {
  filename: string;
  month?: string;
  year?: string;
  comir?: string;
  description?: string;
  uploadDate?: string;
}

// تعریف نوع کارمند
interface Employee {
  id?: string;
  _id?: any;
  firstName: string;
  lastName: string;
  nationalCode: string;
  employeeCode: string;
  homePhone: string;
  mobilePhone: string;
  profileImage: string | null;
  jobGroup: string;
  status: string;
  email: string;
  age: string;
  jobTitle: string;
  salaries: Salary[];
}

// تعریف نوع Context
interface EmployeeContextType {
  employees: Employee[];
  setEmployees: React.Dispatch<React.SetStateAction<Employee[]>>;
  refreshEmployees: () => Promise<void>;
  isLoadingEmployees: boolean;
  hasLoadedEmployees: boolean;
}

const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined);

export const EmployeeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [hasLoadedEmployees, setHasLoadedEmployees] = useState(false);
  const pathname = usePathname();

  const inflight = useRef<AbortController | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttempt = useRef<number>(0);

  // ✅ 1) اول clearRetry را پایدار کن
  const clearRetry = useCallback(() => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  }, []);

  // ✅ 2) بعد scheduleRetry را پایدار کن (وابسته به clearRetry)
  const scheduleRetry = useCallback(
    (fn: () => void) => {
      clearRetry();
      // backoff: 1s, 2s, 4s, 8s, 15s, 30s
      const delays = [1000, 2000, 4000, 8000, 15000, 30000];
      const idx = Math.min(retryAttempt.current, delays.length - 1);
      const delay = delays[idx];
      retryAttempt.current += 1;

      retryTimer.current = setTimeout(fn, delay);
    },
    [clearRetry]
  );

  // ✅ 3) حالا refreshEmployees که از scheduleRetry استفاده می‌کند
  const refreshEmployees = useCallback(async () => {
    // فهرست کامل کارکنان فقط در صفحاتی دریافت شود که واقعاً آن را مصرف می‌کنند.
    const needsEmployeeDirectory =
      pathname === "/employee/dashboard" ||
      pathname.startsWith("/employee/dashboard/") ||
      pathname.startsWith("/employee/salary-all/salary/");
    if (
      !needsEmployeeDirectory ||
      (typeof navigator !== "undefined" && !navigator.onLine)
    ) {
      return;
    }

    try {
      clearRetry();
      setIsLoadingEmployees(true);

      // cancel any inflight request
      inflight.current?.abort();
      const ac = new AbortController();
      inflight.current = ac;

      const res = await fetch("/api/employees", {
        credentials: "include",
        cache: "no-store",
        signal: ac.signal,
      });

      // اگر هنوز sid آماده نشده (خیلی رایج بلافاصله بعد از login redirect)
      if (res.status === 401) {
        scheduleRetry(() => void refreshEmployees());
        return;
      }

      if (!res.ok) {
        throw new Error(`Failed to fetch employees: ${res.status}`);
      }

      const data = await res.json();
      setEmployees(Array.isArray(data) ? data : []);
      setHasLoadedEmployees(true);
      retryAttempt.current = 0;
    } catch (err: any) {
      if (err?.name === "AbortError") return;
      // خطای شبکه (از جمله قطعی DNS سمت کاربر) با retry سریع تشدید نشود.
      if (err instanceof TypeError) return;
      console.error("Employee API error:", err);
      scheduleRetry(() => void refreshEmployees());
    } finally {
      setIsLoadingEmployees(false);
    }
  }, [pathname, scheduleRetry, clearRetry]);

  // وقتی وارد /employee شدی، یا pathname عوض شد، دوباره تلاش کن
  useEffect(() => {
    void refreshEmployees();
    return () => {
      clearRetry();
      inflight.current?.abort();
    };
  }, [refreshEmployees, clearRetry]);

  // کمک اضافی: وقتی تب فوکوس می‌گیرد هم refresh کن
  useEffect(() => {
    const onFocus = () => void refreshEmployees();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshEmployees]);

  return (
    <EmployeeContext.Provider
      value={{
        employees,
        setEmployees,
        refreshEmployees,
        isLoadingEmployees,
        hasLoadedEmployees,
      }}
    >
      {children}
    </EmployeeContext.Provider>
  );
};

export const useEmployeeContext = () => {
  const context = useContext(EmployeeContext);
  if (!context) {
    throw new Error("useEmployeeContext must be used within an EmployeeProvider");
  }
  return context;
};
